import type { ObservableValue } from '@amazon/vinyl-observable'
import { data } from '@amazon/vinyl-observable'
import { globalRef, type LogTarget, type Unsubscribe } from '@amazon/vinyl-util'
import { handleError } from '@/errorHandler'
import { asError } from '@/util/errorUtil'

/** Services the app cannot run without: sign-in identity, logging and the remote error reporter. */
export type FatalSetupSubsystem = 'IdentityStore' | 'Telemetry' | 'ErrorReporter'

export interface FatalSetupFailure {
    readonly subsystem: FatalSetupSubsystem
    readonly error: Error
}

const target: LogTarget = { logPrefix: 'setupFailure' }

const failureRef = globalRef(() => data<FatalSetupFailure | null>(null))

export function fatalSetupFailure$(): ObservableValue<FatalSetupFailure | null> {
    return failureRef.value
}

/** Reports a core service's failure and keeps the first one: later failures cascade from it. */
export function recordFatalSetupFailure(subsystem: FatalSetupSubsystem, error: unknown): void {
    try {
        handleError(target, error)
    } catch {
        // The logger may be the subsystem that failed.
    }
    const failure = failureRef.value
    if (failure.value === null) failure.value = { subsystem, error: asError(error) }
}

/** Runs one core service's setup; a throw is recorded as fatal and the result is undefined. */
export function startCoreService<T>(subsystem: FatalSetupSubsystem, setup: () => T): T | undefined {
    try {
        return setup()
    } catch (error) {
        recordFatalSetupFailure(subsystem, error)
        return undefined
    }
}

/**
 * Takes the page to the error page once a core service has failed, before or after first paint.
 * The error page loads on demand, so a healthy boot never fetches it.
 */
export function installFatalSetupFailurePage(): Unsubscribe {
    return failureRef.value.onData((failure) => {
        if (failure === null) return
        import('@/error/FatalSetupFailurePage')
            .then(({ showFatalSetupFailurePage }) => showFatalSetupFailurePage(failure))
            .catch((error: unknown) => handleError(target, error))
    })
}
